import React, { useEffect, useState } from "react";
import { Fuel, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Brand → dot colour (falls back to slate for anything not mapped)
const BRAND_COLORS = {
  TESCO: "#00539F",
  ASDA: "#78BE20",
  "SAINSBURY'S": "#F06C00",
  SAINSBURYS: "#F06C00",
  MORRISONS: "#007A3D",
  BP: "#009900",
  SHELL: "#FBCE07",
  ESSO: "#0033A0",
  JET: "#7A1D75",
  TEXACO: "#C8102E",
  "EG ON THE MOVE": "#FF5722",
  "CO-OP": "#00B1E7",
  GULF: "#FF6600",
  RONTEC: "#6A1B9A",
};

/**
 * Public homepage trust widget: "Tracking {X} UK stations across {brands…}".
 * Pulls live coverage from /api/fuel/coverage/public. Silently hides on API failure.
 */
export default function LiveFuelCoverageWidget() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/fuel/coverage/public`);
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (!cancelled && d?.success) setData(d);
      } catch (_e) {
        // Silent — widget is decorative trust; don't surface errors on public pages.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded) {
    return (
      <section
        className="border-y border-zinc-100 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 py-5"
        data-testid="live-fuel-coverage-loading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-5 bg-zinc-100 rounded w-72 mx-auto animate-pulse" />
        </div>
      </section>
    );
  }

  // If the API flopped or the DB is empty, render nothing. Don't break the page.
  if (!data || !data.total_stations) return null;

  const stations = data.total_stations;
  const brands = (data.top_brands || []).slice(0, 10);
  const brandChips = brands.slice(0, 6);

  return (
    <section
      className="border-y border-zinc-100 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 py-5 sm:py-6"
      data-testid="live-fuel-coverage-widget"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Fuel className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-emerald-700 font-bold tracking-[0.15em] uppercase">
                Live fuel coverage
              </p>
              <p
                className="text-base sm:text-lg text-zinc-800 font-bold leading-tight"
                style={{ fontFamily: "Outfit,sans-serif" }}
                data-testid="coverage-headline"
              >
                Tracking{" "}
                <span
                  className="text-emerald-700"
                  data-testid="coverage-station-count"
                >
                  {stations.toLocaleString()}
                </span>{" "}
                UK fuel stations across {data.distinct_brands}+ brands
              </p>
              {data.time_ago && (
                <p className="text-[11px] text-zinc-500 mt-0.5" data-testid="coverage-time-ago">
                  Last price refresh · {data.time_ago}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 lg:justify-end">
            {brandChips.map((b) => {
              const key = b.name.toUpperCase();
              const color = BRAND_COLORS[key] || "#52525b";
              return (
                <span
                  key={b.name}
                  className="inline-flex items-center gap-1.5 bg-white border border-zinc-200 rounded-full px-2.5 py-1 text-[11px] font-semibold text-zinc-700 shadow-sm"
                  data-testid={`coverage-brand-chip-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  {b.name}
                  <span className="text-zinc-400 font-medium">
                    {b.count.toLocaleString()}
                  </span>
                </span>
              );
            })}
            <Link
              to="/app?view=fuel-prices"
              className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 text-[11px] font-bold uppercase tracking-wider ml-1"
              data-testid="coverage-cta"
            >
              See prices <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
